import { APP_STORE_URL, PLAY_STORE_URL } from "@/pages/landing/constants";

export type BranePlatform = "ios" | "android" | "desktop";

export type BraneAction =
  /** Hand the Brane to the installed app: the custom scheme on iOS, an intent:// on Android. */
  | { kind: "app"; href: string }
  /** Where to get the app. */
  | { kind: "store"; href: string }
  /** Stay on the web: the send page, or sign-in / sign-up that comes back to it (`returnTo`). */
  | { kind: "web"; to: string; returnTo?: string };

export interface OpenPlan {
  platform: BranePlatform;
  /** The plate. */
  primary: BraneAction;
  /** The text link beside it. */
  secondary?: BraneAction;
  /** On a phone, the quiet way to carry on in the browser instead. */
  web?: BraneAction;
}

/**
 * Which device a visitor is on. iPadOS reports itself as a Mac, so a Mac with a touch
 * screen is an iPad.
 */
export function branePlatform(ua: string, touchPoints = 0): BranePlatform {
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua) && touchPoints > 1) return "ios";
  return "desktop";
}

/**
 * Where a Brane card's actions go, per device.
 *
 * - A phone opens the app. iOS gets the custom scheme as a plain link, with no timed
 *   fallback: Safari asks "Open in aSTRING?" first, and a timer that fires while that
 *   prompt is up sends everyone to the App Store whether they have the app or not. The
 *   App Store is the link beside it instead. Android's intent:// falls back by itself,
 *   to the web, so the send still happens without the app; Google Play is the link beside it.
 * - A computer stays on the web: the send page when signed in, otherwise sign-in that
 *   comes back to it, with sign-up beside it.
 * - On a phone the web is still there, as a quiet link.
 */
export function openPlan({
  handle,
  signedIn,
  ua,
  touchPoints = 0,
  origin = "https://astring.app",
}: {
  handle: string;
  signedIn: boolean;
  ua: string;
  touchPoints?: number;
  origin?: string;
}): OpenPlan {
  const h = encodeURIComponent(handle);
  const send = `/y/b/${h}`;
  const web: BraneAction = signedIn ? { kind: "web", to: send } : { kind: "web", to: "/login", returnTo: send };
  const platform = branePlatform(ua, touchPoints);

  if (platform === "android") {
    const fallback = encodeURIComponent(`${origin}${signedIn ? send : "/login"}`);
    return {
      platform,
      primary: {
        kind: "app",
        href: `intent://b/${h}#Intent;scheme=astring;package=app.astring.app;S.browser_fallback_url=${fallback};end`,
      },
      secondary: { kind: "store", href: PLAY_STORE_URL },
      web,
    };
  }
  if (platform === "ios") {
    return {
      platform,
      primary: { kind: "app", href: `astring://b/${h}` },
      secondary: { kind: "store", href: APP_STORE_URL },
      web,
    };
  }
  return {
    platform,
    primary: web,
    secondary: signedIn ? undefined : { kind: "web", to: "/signup", returnTo: send },
  };
}
